<template>
    <div v-if="pagination !== null && !shouldBeHidden"
         class="d-flex flex-row justify-content-between align-items-center flex-wrap"
    >
        <div class="d-flex flex-wrap py-2 mr-3"
             :class="{'opacity-50 cursor-not-allowed pointer-events-none': shouldBeDisabled}">
            <button type="button" @click="loadPage(1)" :disabled="isOnFirstPage"
                    class="btn btn-icon btn-sm btn-light-primary mr-2 my-1"><i
                class="ki ki-bold-double-arrow-back icon-xs"></i></button>
            <button type="button" @click="loadPage(pagination.current_page - 1)" :disabled="isOnFirstPage"
                    class="btn btn-icon btn-sm btn-light-primary mr-2 my-1"><i
                class="ki ki-bold-arrow-back icon-xs"></i></button>

            <div v-if="notEnoughPages">
                <button type="button" v-for="n in totalPage" :class="{'active': isCurrentPage(n)}" @click="loadPage(n)"
                        class="btn btn-icon btn-sm border-0 btn-default text-dark btn-hover-primary mr-2 my-1"
                        v-html="n"></button>
            </div>
            <div v-else>
                <button type="button" v-show="pagination.current_page > 3" disabled
                        class="btn btn-icon btn-sm border-0 btn-hover-primary mr-2 my-1">...
                </button>
                <button type="button" v-for="n in windowSize" :class="{'active': isCurrentPage(windowStart+n-1)}"
                        class="btn btn-icon btn-sm border-0 btn-default text-dark btn-hover-primary mr-2 my-1"
                        @click="loadPage(windowStart+n-1)" v-html="windowStart+n-1"></button>
                <button type="button" v-show="pagination.last_page - pagination.current_page >= 3" disabled
                        class="btn btn-icon btn-sm border-0 btn-hover-primary mr-2 my-1">...
                </button>
            </div>

            <button type="button" @click="loadPage(pagination.current_page + 1)" :disabled="isOnLastPage"
                    class="btn btn-icon btn-sm btn-light-primary mr-2 my-1"><i
                class="ki ki-bold-arrow-next icon-xs"></i></button>
            <button type="button" @click="loadPage(pagination.last_page)" :disabled="isOnLastPage"
                    class="btn btn-icon btn-sm btn-light-primary mr-2 my-1"><i
                class="ki ki-bold-double-arrow-next icon-xs"></i></button>
        </div>
        <div class="d-flex flex-row-reverse align-items-center py-3">
            <select class="form-control form-control-sm text-primary font-weight-bold ml-4 border-0 bg-light-primary"
                    style="width: 65px;"
                    @change="changePerPage">
                <option v-if="!perPageOptions.includes(pagination.per_page)"
                        :value="pagination.per_page"
                        selected>
                    {{ pagination.per_page }}
                </option>
                <option v-for="perPageOption in perPageOptions"
                        :value="perPageOption"
                        :selected="pagination.per_page === perPageOption">
                    {{ perPageOption }}
                </option>
            </select>

            <span class="text-muted">{{ __('general.show') }} {{ from }} - {{ to }} {{
                    __('general.of')
                }} {{ pagination.total }}</span>

            <div v-if="loading || isCurrentlyLoading" class="d-flex align-items-center mr-4">
                <div class="ml-2 text-muted"></div>
                <div class="spinner spinner-primary mr-10"></div>
            </div>
        </div>
    </div>
</template>

<script>
import {rewriteUrl} from "@composables/rewriteUrl";
import {scrollToTop} from "@composables/scrollToTop";

export default {
    props: {
        loading: Boolean,
        pagination: Object,
        inertiaReload: Boolean,
        hideWhenOnePage: Boolean,
        disableWhenOnePage: Boolean,
        scrollToTop: Boolean,
        inertiaOptions: {
            type: Object,
            default() {
                return {};
            }
        }
    },

    emits: ['update:loading'],

    data() {
        return {
            isCurrentlyLoading: false,
            onEachSide: 2,
            perPageOptions: [10, 20, 50]
        }
    },

    methods: {
        loadPage(page) {
            this.$emit('changePage', page)
            if (this.inertiaReload || this.inertiaOptions !== {}) {
                rewriteUrl('page', page)
                this.isCurrentlyLoading = true;
                this.inertiaOptions.onSuccess = () => {
                    this.isCurrentlyLoading = false;
                    scrollToTop();
                };
                if(this.scrollToTop){
                    this.inertiaOptions.preserveScroll = false;
                }
                this.$inertia.reload(this.inertiaOptions)
                event.target.blur();
            }
        },
        isCurrentPage(page) {
            return page === this.pagination.current_page
        },
        changePerPage(e) {
            this.$emit('changePerPage', e.target.value)
            if (this.inertiaReload || this.inertiaOptions !== {}) {
                rewriteUrl('per_page', e.target.value)
                this.isCurrentlyLoading = true;
                this.inertiaOptions.onSuccess = () => this.isCurrentlyLoading = false;
                this.$inertia.reload(this.inertiaOptions)
            }
        },
    },

    computed: {
        shouldBeHidden() {
            return this.hideWhenOnePage && (this.pagination === null || this.pagination.last_page === 1);
        },
        shouldBeDisabled() {
            return this.disableWhenOnePage && (this.pagination === null || this.pagination.last_page === 1);
        },
        totalPage() {
            return this.pagination === null
                ? 0
                : this.pagination.last_page
        },
        isOnFirstPage() {
            return this.pagination === null
                ? false
                : this.pagination.current_page === 1
        },
        isOnLastPage() {
            return this.pagination === null
                ? false
                : this.pagination.current_page === this.pagination.last_page
        },
        notEnoughPages() {
            return this.pagination.last_page < (this.onEachSide * 2) + 4
        },
        windowSize() {
            if (this.pagination === null) {
                return 0
            }

            return this.onEachSide * 2 + 1
        },
        windowStart() {
            if (this.pagination === null) {
                return 0
            }

            if (!this.pagination || this.pagination.current_page <= this.onEachSide) {
                return 1
            } else if (this.pagination.current_page >= (this.pagination.last_page - this.onEachSide)) {
                return this.pagination.last_page - this.onEachSide * 2
            }

            return this.pagination.current_page - this.onEachSide
        },
        from() {
            return (this.pagination.current_page - 1) * this.pagination.per_page
        },
        to() {
            let to = this.pagination.current_page * this.pagination.per_page
            if (to > this.pagination.total) {
                return this.pagination.total
            }
            return to
        },
    },

    watch: {
        isCurrentlyLoading(newValue, oldValue) {
            this.$emit('update:loading', newValue)
        }
    }
}
</script>
